// ═══════════════════════════════════════════════════════════════════════════════
// briefingContext.ts — typed API client for the Patient Briefing Context surface
// ═══════════════════════════════════════════════════════════════════════════════
//
// Backs the three Agent Studio screens:
//   1. Element Catalog  → BriefingContextElementController
//   2. Briefings list + composition editor → BriefingContextController
//   3. Agent's Patient Briefing tab → AgentBriefingContextController
//
// Every endpoint returns OperationResult<T>; this module unwraps and throws on
// failure so callers get a clean Promise<T>. Mirrors the api/skills.ts pattern.
// ═══════════════════════════════════════════════════════════════════════════════

import { apiDelete, apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ────────────────────────────────────────────────────────────────────────────
// Domain types — mirror C# DTOs in OO.Domain.AIInfo / controllers
// ────────────────────────────────────────────────────────────────────────────

export interface BriefingContextElement {
  id: number;
  code: string;
  displayName?: string;
  description?: string;
  category?: string;
  /** C# class name of the renderer. Documentation only at runtime. */
  rendererClass?: string;
  /** 1 = stable (cached per request scope); 2 = per-turn fresh. */
  tier: number;
  defaultMaxTokens: number;
  sortOrder: number;
  isActive: boolean;
  /**
   * Canonical sample of the text this element injects into the LLM prompt.
   * SME-authored from the Studio editor. Documentation only — not runtime
   * data. NULL on legacy rows pre-Phase-4 (the Studio falls back to the
   * frontend sampleOutputs.ts map when this is null).
   */
  sampleOutput?: string | null;
  createdDate?: string;
  createdBy?: number | null;
  lastUpdatedDate?: string | null;
  updatedBy?: number | null;
}

export interface BriefingContext {
  id: number;
  code: string;
  name?: string;
  description?: string;
  version: number;
  ownerEmployeeId?: number | null;
  merchantId?: number | null;
  isActive: boolean;
  isDraft: boolean;
  publishedAtUtc?: string | null;
  publishedByEmployeeId?: number | null;
  createdDate?: string;
  createdBy?: number | null;
  lastUpdatedDate?: string | null;
  updatedBy?: number | null;
}

export interface BriefingContextComposition {
  id: number;
  briefingContextId: number;
  briefingContextElementId: number;
  sortOrder: number;
  overrideMaxTokens?: number | null;
  isActive: boolean;
  createdDate?: string;
  createdBy?: number | null;
}

/** Composition + joined element details, as returned by Composition/{briefingId}. */
export interface CompositionRow {
  id: number;
  briefingContextId: number;
  briefingContextElementId: number;
  sortOrder: number;
  overrideMaxTokens?: number | null;
  isActive: boolean;
  // Element details (joined)
  elementCode: string;
  elementDisplayName?: string;
  elementCategory?: string;
  elementRendererClass?: string;
  elementTier: number;
  elementDefaultMaxTokens: number;
  elementIsActive: boolean;
}

export interface AgentBriefingLink {
  id: number;
  aiAgentId: number;
  briefingContextId: number;
  sortOrder: number;
  isActive: boolean;
  createdDate?: string;
  createdBy?: number | null;
}

// ════════════════════════════════════════════════════════════════════════════
// 1. ELEMENT CATALOG — /api/BriefingContextElement
// ════════════════════════════════════════════════════════════════════════════

export async function listElements(includeInactive = false): Promise<BriefingContextElement[]> {
  const res = await apiGet<OperationResult<BriefingContextElement[]>>(
    '/api/BriefingContextElement/Search',
    { includeInactive },
  );
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load briefing elements');
  return res.data ?? [];
}

export async function getElementById(id: number): Promise<BriefingContextElement> {
  const res = await apiGet<OperationResult<BriefingContextElement>>(
    `/api/BriefingContextElement/GetById/${id}`,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || `Element ${id} not found`);
  return res.data;
}

export async function getElementByCode(code: string): Promise<BriefingContextElement> {
  const res = await apiGet<OperationResult<BriefingContextElement>>(
    `/api/BriefingContextElement/GetByCode/${encodeURIComponent(code)}`,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || `Element '${code}' not found`);
  return res.data;
}

/**
 * Class names of every IBriefingContextElementProvider registered in DI.
 * Drives the editor's Renderer dropdown. Sourced from code (not SQL), so a
 * stale SQL value is highlighted by the editor as "not found in code".
 */
export async function listRendererClassNames(): Promise<string[]> {
  const res = await apiGet<OperationResult<string[]>>('/api/BriefingContextElement/Renderers');
  if (!res.isSuccess) throw new Error(res.message || 'Failed to enumerate renderers');
  return res.data ?? [];
}

export async function addElement(model: Partial<BriefingContextElement>): Promise<BriefingContextElement> {
  const res = await apiPost<OperationResult<BriefingContextElement>>(
    '/api/BriefingContextElement/Add',
    model,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to create element');
  return res.data;
}

export async function updateElement(model: Partial<BriefingContextElement> & { id: number }): Promise<BriefingContextElement> {
  const res = await apiPost<OperationResult<BriefingContextElement>>(
    '/api/BriefingContextElement/Update',
    model,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to update element');
  return res.data;
}

/**
 * Row-level kill switch. Idempotent — flipping to the current state is a
 * no-op on the server. Returns the new state for optimistic UI confirmation.
 */
export async function setElementActive(id: number, isActive: boolean): Promise<{ id: number; isActive: boolean }> {
  const res = await apiPost<OperationResult<{ id: number; isActive: boolean }>>(
    `/api/BriefingContextElement/SetActive/${id}`,
    { isActive },
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to set active state');
  return res.data;
}

// ════════════════════════════════════════════════════════════════════════════
// 2. BRIEFINGS + COMPOSITION — /api/BriefingContext
// ════════════════════════════════════════════════════════════════════════════

export interface SearchBriefingsParams {
  /** Include rows where IsActive = false (kill-switched). */
  includeInactive?: boolean;
  /** Include rows where IsDraft = true. Studio's default view typically passes true. */
  includeDrafts?: boolean;
}

export async function listBriefings(params: SearchBriefingsParams = {}): Promise<BriefingContext[]> {
  const { includeInactive = false, includeDrafts = false } = params;
  const res = await apiGet<OperationResult<BriefingContext[]>>('/api/BriefingContext/Search', {
    includeInactive,
    includeDrafts,
  });
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load briefings');
  return res.data ?? [];
}

export async function getBriefingById(id: number): Promise<BriefingContext> {
  const res = await apiGet<OperationResult<BriefingContext>>(`/api/BriefingContext/GetById/${id}`);
  if (!res.isSuccess || !res.data) throw new Error(res.message || `Briefing ${id} not found`);
  return res.data;
}

/**
 * Create a new briefing as a DRAFT. Drafts are invisible to the runtime
 * until the caller hits Publish. Code must be unique within scope
 * (global vs MerchantId).
 */
export async function createBriefingDraft(model: Partial<BriefingContext>): Promise<BriefingContext> {
  const res = await apiPost<OperationResult<BriefingContext>>('/api/BriefingContext/Add', model);
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to create briefing');
  return res.data;
}

/**
 * Update mutable metadata only — the server ignores Code and lifecycle
 * fields (IsDraft / Version / PublishedAtUtc / IsActive) on this call.
 * Use {@link setBriefingActive} for kill-switch and {@link publishBriefing}
 * for the draft→published gesture.
 */
export async function updateBriefing(model: Partial<BriefingContext> & { id: number }): Promise<BriefingContext> {
  const res = await apiPost<OperationResult<BriefingContext>>('/api/BriefingContext/Update', model);
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to update briefing');
  return res.data;
}

export async function setBriefingActive(id: number, isActive: boolean): Promise<{ id: number; isActive: boolean }> {
  const res = await apiPost<OperationResult<{ id: number; isActive: boolean }>>(
    `/api/BriefingContext/SetActive/${id}`,
    { isActive },
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to set active state');
  return res.data;
}

/**
 * Promote draft → published OR re-publish after an edit. Each call bumps
 * Version by 1 and stamps PublishedAtUtc / PublishedBy. Returns the
 * refreshed briefing so the UI can update without a second round-trip.
 */
export async function publishBriefing(id: number): Promise<BriefingContext> {
  const res = await apiPost<OperationResult<BriefingContext>>(`/api/BriefingContext/Publish/${id}`);
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to publish briefing');
  return res.data;
}

// ── Composition ─────────────────────────────────────────────────────────────

export async function getComposition(briefingId: number): Promise<CompositionRow[]> {
  const res = await apiGet<OperationResult<CompositionRow[]>>(
    `/api/BriefingContext/Composition/${briefingId}`,
  );
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load composition');
  return res.data ?? [];
}

export interface AddCompositionRequest {
  briefingContextId: number;
  elementId: number;
  sortOrder: number;
  overrideMaxTokens?: number | null;
}

export async function addCompositionElement(req: AddCompositionRequest): Promise<BriefingContextComposition> {
  const res = await apiPost<OperationResult<BriefingContextComposition>>(
    '/api/BriefingContext/Composition/Add',
    req,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to add element');
  return res.data;
}

export async function updateComposition(
  model: Partial<BriefingContextComposition> & { id: number },
): Promise<BriefingContextComposition> {
  const res = await apiPost<OperationResult<BriefingContextComposition>>(
    '/api/BriefingContext/Composition/Update',
    model,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to update composition');
  return res.data;
}

export async function removeCompositionElement(compositionId: number): Promise<{ id: number }> {
  const res = await apiDelete<OperationResult<{ id: number }>>(
    `/api/BriefingContext/Composition/Remove/${compositionId}`,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to remove element');
  return res.data;
}

/**
 * Drag-to-reorder. Server rewrites SortOrder to match the posted sequence
 * (1, 2, 3, ...). Loud-fails if any id doesn't belong to the briefing.
 */
export async function reorderComposition(
  briefingId: number,
  orderedIds: number[],
): Promise<void> {
  const res = await apiPost<OperationResult<unknown>>(
    `/api/BriefingContext/Composition/Reorder/${briefingId}`,
    { orderedIds },
  );
  if (!res.isSuccess) throw new Error(res.message || 'Reorder failed');
}

// ════════════════════════════════════════════════════════════════════════════
// 3. AGENT ↔ BRIEFING LINK — /api/AgentBriefingContext
// ════════════════════════════════════════════════════════════════════════════

export async function listLinksForAgent(
  aiAgentId: number,
  includeInactive = true,
): Promise<AgentBriefingLink[]> {
  const res = await apiGet<OperationResult<AgentBriefingLink[]>>(
    `/api/AgentBriefingContext/Search/${aiAgentId}`,
    { includeInactive },
  );
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load briefing links');
  return res.data ?? [];
}

export async function linkBriefingToAgent(
  aiAgentId: number,
  briefingContextId: number,
  sortOrder: number,
): Promise<AgentBriefingLink> {
  const res = await apiPost<OperationResult<AgentBriefingLink>>('/api/AgentBriefingContext/Link', {
    aiAgentId,
    briefingContextId,
    sortOrder,
  });
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to link briefing');
  return res.data;
}

export async function setLinkActive(
  id: number,
  isActive: boolean,
): Promise<{ id: number; isActive: boolean }> {
  const res = await apiPost<OperationResult<{ id: number; isActive: boolean }>>(
    `/api/AgentBriefingContext/SetActive/${id}`,
    { isActive },
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to set link active state');
  return res.data;
}

export async function reorderLinks(aiAgentId: number, orderedIds: number[]): Promise<void> {
  const res = await apiPost<OperationResult<unknown>>(
    `/api/AgentBriefingContext/Reorder/${aiAgentId}`,
    { orderedIds },
  );
  if (!res.isSuccess) throw new Error(res.message || 'Reorder failed');
}

export async function unlinkBriefingFromAgent(id: number): Promise<{ id: number }> {
  const res = await apiDelete<OperationResult<{ id: number }>>(
    `/api/AgentBriefingContext/Unlink/${id}`,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to unlink');
  return res.data;
}

/**
 * Master "disable patient briefing for this agent" gesture. Flips IsActive
 * to false on every link row for the agent in one call; returns the count
 * of rows affected so the UI can confirm ("disabled 5 briefings").
 */
export async function disableAllBriefingsForAgent(
  aiAgentId: number,
): Promise<{ agentId: number; affected: number }> {
  const res = await apiPost<OperationResult<{ agentId: number; affected: number }>>(
    `/api/AgentBriefingContext/DisableAllForAgent/${aiAgentId}`,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to disable all briefings');
  return res.data;
}

// ────────────────────────────────────────────────────────────────────────────
// LLM-driven suggestion surface — POST /Suggest/{aiAgentId}
// ────────────────────────────────────────────────────────────────────────────

/** One ranked recommendation in a Suggest response. */
export interface BriefingSuggestion {
  /** ai.BriefingContext.Id — used by the drawer's "Link" button. */
  briefingContextId: number;
  /** Stable Code (e.g. "folfox-patient-context"). */
  code: string;
  /** Human-readable name; what the SME sees in the card title. */
  name?: string;
  /** 1–10 inclusive. Higher = more relevant. */
  relevance: number;
  /** Short SME-readable rationale. No code names, plain English. */
  reasoning: string;
}

/** Full Suggest payload — list + already-linked ids + provenance metadata. */
export interface BriefingSuggestionResult {
  agentId: number;
  agentName?: string;
  suggestions: BriefingSuggestion[];
  /** Briefing ids already linked to the agent (active OR inactive). */
  alreadyLinkedBriefingIds: number[];
  /** Model identifier (e.g. "gpt-4.1-mini"). Surfaced in drawer footer. */
  modelUsed?: string | null;
  /** Wall-clock duration of the LLM round-trip, milliseconds. */
  elapsedMs: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Live-preview surface — POST /Preview/{aiAgentId}  (Phase 8)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Per-element row in a Preview response. One row per (briefing, element) pair
 * AFTER dedup — the loser still appears with `status = 'Deduped'` and
 * `dedupedFromBriefingCode` pointing at the briefing that won.
 */
export interface BriefingPreviewElement {
  elementCode: string;
  displayName?: string;
  category?: string;
  /** 1 = stable / 2 = per-turn. */
  tier: number;
  maxTokens: number;
  /** 'Rendered' | 'Empty' | 'Deduped' | 'NoProvider' | 'Fault'. */
  status: 'Rendered' | 'Empty' | 'Deduped' | 'NoProvider' | 'Fault';
  /** The text the LLM would see. Null on non-Rendered statuses. */
  text?: string | null;
  /** Briefing Code that won the dedup contest. Only set when status='Deduped'. */
  dedupedFromBriefingCode?: string | null;
  /** Provider wall-clock duration, ms. 0 for non-Rendered. */
  elapsedMs: number;
  /** Friendly fault message; only present when status='Fault'. */
  faultMessage?: string | null;
  /** C# class name (Advanced toggle only). */
  rendererClass?: string;
}

export interface BriefingPreviewBriefing {
  briefingContextId: number;
  code: string;
  name?: string;
  version: number;
  sortOrder: number;
  elements: BriefingPreviewElement[];
}

export interface BriefingPreviewResult {
  agentId: number;
  agentName?: string;
  memberId: number;
  /** Initials only (e.g. "J.M.") — PHI-minimized header. */
  memberInitials?: string;
  memberAge?: number | null;
  memberSex?: string;
  memberLanguageCode?: string;
  /** "•••4521" — masked last-4. PHI-minimized. */
  memberMrnMasked?: string;
  briefings: BriefingPreviewBriefing[];
  // Totals
  renderedCount: number;
  emptyCount: number;
  dedupedCount: number;
  noProviderCount: number;
  faultCount: number;
  elapsedMs: number;
  /** Byte-equivalent of what the LLM sees in the system-prompt briefing block. */
  combinedPromptText?: string;
}

/**
 * Render the agent's briefing pipeline against a chosen patient and return
 * the per-element status + text + timing.
 *
 * <p>
 * HTTP POST so memberId stays out of URLs (PHI in logs/proxies).
 *
 * <p>
 * Soft-failure: provider faults / timeouts surface as per-element
 * `status='Fault'` rows. Hard validation failures (missing agent /
 * member / cross-tenant) come back as `isSuccess=false` with a friendly
 * inline message that the drawer renders.
 */
export async function previewBriefingForAgent(
  aiAgentId: number,
  memberId: number,
): Promise<BriefingPreviewResult> {
  const res = await apiPost<OperationResult<BriefingPreviewResult>>(
    `/api/AgentBriefingContext/Preview/${aiAgentId}`,
    { memberId },
  );
  if (!res.isSuccess || !res.data) {
    throw new Error(res.message || 'Failed to render preview');
  }
  return res.data;
}

/**
 * Per-element preview returned by `previewBriefingElement`. Mirrors the
 * `ElementPreviewResult` C# DTO in `OO.Business/AIInfo/BriefingContextInfo/BriefingPreview.cs`.
 */
export interface ElementPreviewResult {
  elementId: number;
  elementCode: string;
  displayName?: string;
  category?: string;
  tier: number;
  maxTokens: number;
  rendererClass?: string;
  /** Catalog SampleOutput. Null on legacy rows. */
  canonicalSample?: string | null;
  /** 'Rendered' | 'Empty' | 'Fault' | 'NoProvider'. */
  status: 'Rendered' | 'Empty' | 'Fault' | 'NoProvider';
  text?: string | null;
  elapsedMs: number;
  faultMessage?: string | null;
  // Patient header (PHI-minimized)
  memberId: number;
  memberInitials?: string;
  memberAge?: number | null;
  memberSex?: string;
  memberLanguageCode?: string;
  memberMrnMasked?: string;
}

/**
 * Render a single catalog ELEMENT against a chosen patient. Backs the
 * Studio Element-Catalog "Preview" debug drawer.
 *
 * <p>HTTP POST so memberId stays out of URLs (PHI in logs/proxies).
 * <p>Provider faults surface as `status='Fault'` rows; this function only
 *    throws on hard failures (network, validation, cross-tenant guard).
 */
export async function previewBriefingElement(
  elementId: number,
  memberId: number,
): Promise<ElementPreviewResult> {
  const res = await apiPost<OperationResult<ElementPreviewResult>>(
    `/api/BriefingContextElement/Preview/${elementId}`,
    { memberId },
  );
  if (!res.isSuccess || !res.data) {
    throw new Error(res.message || 'Element preview failed');
  }
  return res.data;
}

/**
 * Render a single BRIEFING (its full composition) against a chosen patient,
 * independent of any agent linkage. Backs the Studio Briefings-list
 * "Preview" debug drawer.
 *
 * <p>Returns the same shape as `previewBriefingForAgent` with `briefings`
 *    holding exactly one entry.
 */
export async function previewBriefing(
  briefingId: number,
  memberId: number,
): Promise<BriefingPreviewResult> {
  const res = await apiPost<OperationResult<BriefingPreviewResult>>(
    `/api/BriefingContext/Preview/${briefingId}`,
    { memberId },
  );
  if (!res.isSuccess || !res.data) {
    throw new Error(res.message || 'Briefing preview failed');
  }
  return res.data;
}

// ────────────────────────────────────────────────────────────────────────────
// Member-picker helper — reuses /api/MerchantMember/Search
// ────────────────────────────────────────────────────────────────────────────

/** Single hit in the member-search response. */
export interface MemberSearchHit {
  id: number;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  mrn?: string;
  /** ISO date string. */
  dob?: string;
  gender?: string;
  email?: string;
  languageCode?: string;
}

/**
 * Search patients of one merchant by name. Backs the patient pickers (Live Preview,
 * briefings, the validation run). Searches the text as a last name and as a first name,
 * and a full name ("Eric Smith", "Smith, Eric", "Smith Eric") also as first AND last
 * name together, which the endpoint ANDs. Every call asks for A→Z: the endpoint's
 * default is Z→A by first name, which pushed a common name like Eric Smith off the page
 * behind names that merely contain it (2026-11-01). Hits are merged and ranked, the exact
 * name first, then names that start with the text, then the rest. Returns at most
 * `pageSize` patients.
 */
export async function searchMembersForPreview(
  merchantId: number,
  text: string,
  pageSize = 20,
): Promise<MemberSearchHit[]> {
  const query = text.trim().replace(/\s*,\s*/g, ', ').replace(/\s+/g, ' ');
  if (!query) return [];
  const filters: MemberNameFilter[] = fullNameSplits(query).map(([firstName, lastName]) => (
    { firstName, lastName, sortField: 'LastName' }
  ));
  // No name holds a comma, so "Smith, Eric" is only ever a last + first name.
  if (!query.includes(',')) {
    filters.push({ lastName: query, sortField: 'LastName' }, { firstName: query, sortField: 'FirstName' });
  }
  const pages = await Promise.all(filters.map((f) => fetchMemberPage(merchantId, f, pageSize, 1)));
  const seen = new Set<number>();
  return pages
    .flatMap((p) => p.hits)
    .filter((h) => (seen.has(h.id) ? false : (seen.add(h.id), true)))
    .map((hit, arrival) => ({ hit, arrival, rank: nameMatchRank(hit, query) }))
    .sort((a, b) => a.rank - b.rank || a.arrival - b.arrival)
    .slice(0, pageSize)
    .map(({ hit }) => hit);
}

/** The first + last name pairs a typed full name can mean; none for a single word. */
function fullNameSplits(query: string): Array<[first: string, last: string]> {
  const comma = query.indexOf(',');
  if (comma >= 0) {
    const last = query.slice(0, comma).trim();
    const first = query.slice(comma + 1).trim();
    return first && last ? [[first, last]] : [];
  }
  const words = query.split(' ');
  const splits: Array<[first: string, last: string]> = [];
  // "Mary Ann Smith" may be "Mary" + "Ann Smith" or "Mary Ann" + "Smith".
  for (let i = 1; i < words.length && i <= 3; i++) {
    splits.push([words.slice(0, i).join(' '), words.slice(i).join(' ')]);
  }
  if (words.length === 2) splits.push([words[1], words[0]]);
  return splits;
}

/** 0 = the patient's name is the text, 1 = it starts with the text, 2 = it only contains it. */
function nameMatchRank(hit: MemberSearchHit, query: string): number {
  const q = query.toLowerCase();
  const first = (hit.firstName ?? '').toLowerCase();
  const last = (hit.lastName ?? '').toLowerCase();
  const names = [`${first} ${last}`, `${last} ${first}`, `${last}, ${first}`, first, last];
  if (names.includes(q)) return 0;
  return names.some((n) => n.startsWith(q)) ? 1 : 2;
}

/** A page of one merchant's patients, and how many the merchant has in all (null when not said). */
export interface MemberPage {
  hits: MemberSearchHit[];
  total: number | null;
}

/**
 * One page of a merchant's patients, A→Z by last name, with no name filter — for a picker
 * to browse before anything is typed.
 */
export async function listMembersForPicker(
  merchantId: number,
  pageNo: number,
  pageSize: number,
): Promise<MemberPage> {
  return fetchMemberPage(merchantId, { sortField: 'LastName' }, pageSize, pageNo);
}

interface MemberNameFilter {
  firstName?: string;
  lastName?: string;
  /** The column the endpoint orders by; always A→Z. */
  sortField: 'FirstName' | 'LastName';
}

/** The row as it arrives: the client leaves ALL-CAPS keys as sent, so the server's DOB and MRN stay upper case. */
type MemberSearchWire = MemberSearchHit & { DOB?: string; MRN?: string };

async function fetchMemberPage(
  merchantId: number,
  filter: MemberNameFilter,
  pageSize: number,
  pageNo: number,
): Promise<MemberPage> {
  // /api/MerchantMember/Search returns OperationResult<PagedResult<Member>>, which
  // serializes as { data: Member[], paging } (OO.Domain PagedResult). This used to read
  // `items`, which that shape never has, so every search came back empty (2026-10-31).
  const res = await apiGet<
    OperationResult<
      | { data?: MemberSearchWire[]; items?: MemberSearchWire[]; paging?: { totalRecordCount?: number } }
      | MemberSearchWire[]
    >
  >('/api/MerchantMember/Search', {
    merchantId,
    ...filter,
    sortDir: 'ASC',
    pageNo,
    pageSize,
  });
  if (!res.isSuccess) throw new Error(res.message || 'Member search failed');
  if (!res.data) return { hits: [], total: null };
  const rows = Array.isArray(res.data) ? res.data : (res.data.data ?? res.data.items ?? []);
  const total = Array.isArray(res.data) ? null : (res.data.paging?.totalRecordCount ?? null);
  // `dob` / `mrn` read nothing before 2026-11-01: the server sends DOB and MRN, so no picker ever showed an age.
  return {
    hits: rows.map(({ DOB, MRN, ...hit }) => ({ ...hit, dob: hit.dob ?? DOB, mrn: hit.mrn ?? MRN })),
    total,
  };
}

/**
 * Ask the backend for an LLM-ranked list of catalog briefings that would
 * be a good fit for this agent. The backend pulls the agent's instructions,
 * walks every published briefing's composition (element descriptions), and
 * round-trips a small LLM with structured-output formatting.
 *
 * <p>
 * Soft-failure semantics: when the LLM call itself fails (timeout, malformed
 * JSON), the backend still returns isSuccess=true with an empty `suggestions`
 * list. Hard-validation failures (agent missing, no instructions, no
 * published briefings in scope) come back as isSuccess=false with an inline
 * message that the drawer renders.
 *
 * <p>
 * Backend route: <c>POST /api/AgentBriefingContext/Suggest/{aiAgentId}</c>.
 * Server-side cost is one LLM call (~3–5s); UI must show a loading state.
 */
export async function suggestBriefingsForAgent(
  aiAgentId: number,
): Promise<BriefingSuggestionResult> {
  const res = await apiPost<OperationResult<BriefingSuggestionResult>>(
    `/api/AgentBriefingContext/Suggest/${aiAgentId}`,
  );
  if (!res.isSuccess || !res.data) {
    throw new Error(res.message || 'Failed to load suggestions');
  }
  return res.data;
}
